import { Tooltip, TooltipContent, TooltipTrigger } from "@design-system/shadcn";
import React, { useEffect, useRef, useState } from "react";

interface TruncatedCell {
  text: string;
  rect: { top: number; left: number; width: number; height: number };
}

interface CellTruncationTooltipProps {
  /** The `Table`'s own container; every `td` under it is delegated from here. */
  containerRef: React.RefObject<HTMLElement | null>;
}

function readTruncatedCell(cell: HTMLTableCellElement): TruncatedCell | null {
  // Measured, not guessed: whether a value fits depends on the column's width at
  // this table width, which no string-length rule can answer.
  if (cell.scrollWidth <= cell.clientWidth) return null;

  const text = cell.textContent?.trim();
  if (!text) return null;

  const { top, left, width, height } = cell.getBoundingClientRect();
  return { text, rect: { top, left, width, height } };
}

/**
 * The full value of a clipped cell, on hover, under `layout="fixed"`.
 *
 * ONE tooltip for the whole table, moved to whichever cell the pointer is over,
 * rather than a tooltip per cell: a 24-row table is a few hundred cells, and the
 * alternative — upgrading a cell to a trigger on first hover — rebuilds that cell's
 * children under a pointer that is already inside it. Delegation leaves every `td`
 * a plain `td` for the life of the table, so sorts and page turns move nothing.
 */
function CellTruncationTooltip({ containerRef }: CellTruncationTooltipProps) {
  const [anchoredCell, setAnchoredCell] = useState<TruncatedCell | null>(null);
  const [isOpen, setIsOpen] = useState(false);
  const hovered = useRef<HTMLTableCellElement | null>(null);

  useEffect(() => {
    const container = containerRef.current;
    if (!container) return;

    const open = (event: PointerEvent) => {
      const target = event.target;
      const next = target instanceof Element ? target.closest("td") : null;
      // Crossing a badge or a button INSIDE a cell is another `pointerover`; without
      // this the tooltip would be rebuilt and repositioned for each one.
      if (next === hovered.current) return;

      hovered.current = next;

      const truncated = next ? readTruncatedCell(next) : null;
      if (truncated) setAnchoredCell(truncated);
      setIsOpen(truncated !== null);
    };
    const close = () => {
      hovered.current = null;
      setIsOpen(false);
    };

    // `pointerover` bubbles where `pointerenter` does not, which is what makes one
    // listener enough for every cell.
    container.addEventListener("pointerover", open);
    container.addEventListener("pointerleave", close);
    // The anchor is placed in viewport coordinates, so it stops describing its cell
    // the moment anything scrolls.
    window.addEventListener("scroll", close, true);

    return () => {
      container.removeEventListener("pointerover", open);
      container.removeEventListener("pointerleave", close);
      window.removeEventListener("scroll", close, true);
    };
  }, [containerRef]);

  return (
    <Tooltip
      open={isOpen}
      onOpenChange={(nextOpen) => {
        if (!nextOpen) setIsOpen(false);
      }}
    >
      {/* Radix anchors to a node it owns, and the cells have to stay plain `td`s,
          so the trigger is an inert proxy laid over the cell being described. */}
      <TooltipTrigger asChild>
        <span
          aria-hidden
          data-testid="cell-truncation-anchor"
          className="pointer-events-none fixed"
          style={anchoredCell?.rect}
        />
      </TooltipTrigger>
      <TooltipContent>{anchoredCell?.text}</TooltipContent>
    </Tooltip>
  );
}

export { CellTruncationTooltip };
export type { CellTruncationTooltipProps };
